import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { VODVideo } from '../../../interfaces/vod'
import { BehaviorSubject, debounceTime, EMPTY, filter, Subscription, switchMap, tap } from 'rxjs'
import { EventService } from '../../../services/event.service'

@Component({
  selector: 'app-vod-progress',
  standalone: true,
  imports: [CommonModule, MatProgressBarModule],
  templateUrl: './vod-progress.component.html',
  styleUrls: ['./vod-progress.component.scss'],
})
export class VODProgressComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription()
  @Input() vod!: VODVideo
  @Output() doPause = new EventEmitter<void>()
  @Output() doPlay = new EventEmitter<void>()
  @Output() seekTo = new EventEmitter<number>()

  duration?: number
  durationStr?: string
  durationSubscription?: Subscription

  currentTime?: number
  currentTimeStr?: string
  currentTimeSubscription?: Subscription

  previewProgress = 0
  isSeeking = false

  get progress() {
    if (this.currentTime != undefined && this.duration != undefined) {
      return (this.currentTime / this.duration) * 100
    } else {
      return 0
    }
  }
  get previewPercent() {
    if (this.currentTime != undefined && this.duration != undefined) {
      return (this.previewProgress / this.duration) * 100
    } else {
      return 0
    }
  }

  get previewChanged(): boolean {
    if (this.currentTime != undefined) {
      return this.previewProgress != this.currentTime
    } else {
      return false
    }
  }

  private step = 10000
  focused$ = new BehaviorSubject<boolean>(false)

  constructor(
    private cdr: ChangeDetectorRef,
    private eventService: EventService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.vod.state?.duration$.subscribe(duration => {
        this.duration = duration
        if (this.duration) {
          this.durationStr = this.formatToTime(this.duration)
        } else {
          this.durationStr = undefined
        }
        this.cdr.detectChanges()
      })
    )

    this.subscriptions.add(
      this.vod.state?.currentTime$.subscribe(currentplaytime => {
        this.currentTime = currentplaytime
        if (this.currentTime != undefined) {
          this.currentTimeStr = this.formatToTime(this.currentTime)
          if (!this.isSeeking) {
            this.previewProgress = this.currentTime
          }
        } else {
          this.currentTimeStr = undefined
        }
        this.cdr.detectChanges()
      })
    )

    this.subscriptions.add(
      this.focused$
        .pipe(
          switchMap(focused => (focused ? this.eventService.keyDown : EMPTY)),
          filter(key => ['ARROW-RIGHT', 'ARROW-LEFT'].includes(key)),
          tap(key => {
            if (!this.isSeeking) {
              this.doPause.emit()
            }
            this.isSeeking = true
            const delta = key === 'ARROW-RIGHT' ? this.step : -this.step
            if (this.duration != undefined) {
              this.previewProgress = Math.min(Math.max(this.previewProgress + delta, 0), this.duration)
              this.currentTimeStr = this.formatToTime(this.previewProgress)
            }
          }),
          debounceTime(300)
        )
        .subscribe(() => {
          this.seekTo.emit(this.previewProgress)
          this.isSeeking = false
        })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe()
  }

  formatToTime(duration: number): string {
    const seconds = Math.floor(duration / 1000)
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0')
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`
  }

  onFocus(): void {
    this.previewProgress = this.currentTime ?? 0
    this.focused$.next(true)
  }

  onBlur(): void {
    this.focused$.next(false)
  }
}
